import { useQuery, useMutation } from "@apollo/client/react"
import { Button, CircularProgress, Alert, IconButton } from "@mui/material"
import { UserPlus, X } from "lucide-mui"
import React, { useEffect } from "react"
import { useNavigate } from "react-router-dom"

import CreateUserDialog from "@/components/settings/users/create_user_dialog"
import DeleteUserDialog from "@/components/settings/users/delete_user_dialog"
import EditUserDialog from "@/components/settings/users/edit_user_dialog"
import UsersTable from "@/components/settings/users/users_table"
import { useVenue } from "@/contexts/venue"
import {
  GetUsersDocument,
  type GetUsersQuery,
  type GetUsersQueryVariables,
  CreateUserDocument,
  type CreateUserMutation,
  type CreateUserMutationVariables,
  UpdateUserDocument,
  type UpdateUserMutation,
  type UpdateUserMutationVariables,
  DeleteUserDocument,
  type DeleteUserMutation,
  type DeleteUserMutationVariables,
  UserSortField,
} from "@/graphql/graphql"
import i18n from "@/i18n"
import { usePanelLink } from "@/shared/hooks"

import { useUsersState, type User } from "./use_users_state"

const STYLES = {
  loadingContainer: "flex justify-center items-center min-h-screen",
  errorWrapper: "p-8",
  page: "flex flex-col gap-6 max-w-4xl",
  header: "flex justify-between items-center",
  title: "text-3xl font-bold mb-0",
  closeIcon: "w-5 h-5",
} as const

const UserList: React.FC = () => {
  const { venue } = useVenue()
  const navigate = useNavigate()
  const linkTo = usePanelLink()
  const [state, dispatch] = useUsersState()
  const { sortBy, sortDirection, dialog, selectedUser, successMessage, errorMessage } = state

  useEffect(() => {
    if (!venue.permissions?.manageUsers) {
      navigate(linkTo("settings"))
    }
  }, [venue.permissions?.manageUsers, navigate, linkTo])

  const { data, loading, error, refetch } = useQuery<GetUsersQuery, GetUsersQueryVariables>(GetUsersDocument, {
    variables: { sortBy, sortDirection },
  })

  const [createUserMutation, { loading: creating }] = useMutation<CreateUserMutation, CreateUserMutationVariables>(
    CreateUserDocument,
  )
  const [updateUserMutation, { loading: updating }] = useMutation<UpdateUserMutation, UpdateUserMutationVariables>(
    UpdateUserDocument,
  )
  const [deleteUserMutation, { loading: deleting }] = useMutation<DeleteUserMutation, DeleteUserMutationVariables>(
    DeleteUserDocument,
  )

  const handleSort = (field: UserSortField) => dispatch({ type: "TOGGLE_SORT", field })

  const handleCreateUser = async (values, { resetForm }) => {
    dispatch({ type: "CLEAR_MESSAGES" })
    try {
      const result = await createUserMutation({
        variables: {
          firstName: values.firstName,
          lastName: values.lastName,
          email: values.email,
          password: values.password,
          passwordConfirmation: values.passwordConfirmation,
          address: values.address,
          city: values.city,
          country: values.country,
          phone: values.phone,
          position: values.position,
          postcode: values.postcode,
          roles: values.roles?.map((p: any) => ({ venueId: p.venueId, role: p.role })),
        },
      })
      if (result.data?.createUser?.success) {
        resetForm()
        dispatch({ type: "CLOSE_DIALOG" })
        void refetch()
        dispatch({ type: "SET_SUCCESS", message: i18n.t("app.settings.users.list.success.created") })
      } else {
        const errors = result.data?.createUser?.errors
        const errorCode = errors?.[0]?.code
        const errorKey = errorCode
          ? `app.settings.users.list.errors.${errorCode.toLowerCase()}`
          : "app.settings.users.list.errors.unknown"
        dispatch({ type: "SET_ERROR", message: i18n.t(errorKey) })
      }
    } catch (err) {
      console.error("Error creating user:", err)
      dispatch({ type: "SET_ERROR", message: i18n.t("app.settings.users.list.errors.generic") })
    }
  }

  const handleUpdateUser = async (values) => {
    if (!selectedUser) return
    dispatch({ type: "CLEAR_MESSAGES" })
    try {
      const result = await updateUserMutation({
        variables: {
          userId: selectedUser.id,
          firstName: values.firstName,
          lastName: values.lastName,
          email: values.email,
          address: values.address,
          city: values.city,
          country: values.country,
          phone: values.phone,
          position: values.position,
          postcode: values.postcode,
          avatar: values.avatarFile,
          roles: values.roles?.map((p: any) => ({ venueId: p.venueId, role: p.role })),
        },
      })
      if (result.data?.updateUser?.success) {
        dispatch({ type: "CLOSE_DIALOG" })
        void refetch()
        dispatch({ type: "SET_SUCCESS", message: i18n.t("app.settings.users.list.success.updated") })
      } else {
        const errors = result.data?.updateUser?.errors
        const errorCode = errors?.[0]?.code
        const errorKey = errorCode
          ? `app.settings.users.list.errors.${errorCode.toLowerCase()}`
          : "app.settings.users.list.errors.unknown"
        dispatch({ type: "SET_ERROR", message: i18n.t(errorKey) })
      }
    } catch (err) {
      console.error("Error updating user:", err)
      dispatch({ type: "SET_ERROR", message: i18n.t("app.settings.users.list.errors.generic") })
    }
  }

  const handleDeleteUser = async () => {
    if (!selectedUser) return
    dispatch({ type: "CLEAR_MESSAGES" })
    try {
      const result = await deleteUserMutation({ variables: { userId: selectedUser.id } })
      if (result.data?.deleteUser?.success) {
        dispatch({ type: "CLOSE_DIALOG" })
        void refetch()
        dispatch({ type: "SET_SUCCESS", message: i18n.t("app.settings.users.list.success.deleted") })
      } else {
        const errors = result.data?.deleteUser?.errors
        const errorCode = errors?.[0]?.code
        const errorKey = errorCode
          ? `app.settings.users.list.errors.${errorCode.toLowerCase()}`
          : "app.settings.users.list.errors.unknown"
        dispatch({ type: "SET_ERROR", message: i18n.t(errorKey) })
      }
    } catch (err) {
      console.error("Error deleting user:", err)
      dispatch({ type: "SET_ERROR", message: i18n.t("app.settings.users.list.errors.generic") })
    }
  }

  if (loading) {
    return (
      <div className={STYLES.loadingContainer}>
        <CircularProgress />
      </div>
    )
  }

  if (error) {
    return (
      <div className={STYLES.errorWrapper}>
        <Alert severity="error">Error loading users: {error.message}</Alert>
      </div>
    )
  }

  const users = data?.users?.collection || []

  return (
    <div className={STYLES.page} data-testid="settings-users-page">
      <div className={STYLES.header}>
        <h1 className={STYLES.title} data-testid="settings-users-title">
          {i18n.t("app.settings.users.list.title")}
        </h1>
        <Button
          variant="contained"
          color="primary"
          startIcon={<UserPlus />}
          onClick={() => dispatch({ type: "OPEN_CREATE" })}
          data-testid="settings-users-btn-add"
        >
          {i18n.t("app.settings.users.list.add_user")}
        </Button>
      </div>

      {successMessage ? (
        <Alert
          severity="success"
          data-testid="settings-users-alert-success"
          action={
            <IconButton
              aria-label="close"
              color="inherit"
              size="small"
              onClick={() => dispatch({ type: "CLEAR_MESSAGES" })}
            >
              <X className={STYLES.closeIcon} />
            </IconButton>
          }
        >
          {successMessage}
        </Alert>
      ) : null}

      {errorMessage ? (
        <Alert
          severity="error"
          data-testid="settings-users-alert-error"
          action={
            <IconButton
              aria-label="close"
              color="inherit"
              size="small"
              onClick={() => dispatch({ type: "CLEAR_MESSAGES" })}
            >
              <X className={STYLES.closeIcon} />
            </IconButton>
          }
        >
          {errorMessage}
        </Alert>
      ) : null}

      <UsersTable
        users={users}
        sortBy={sortBy}
        sortDirection={sortDirection}
        onSort={handleSort}
        onEdit={(user: User) => dispatch({ type: "OPEN_EDIT", user })}
        onDelete={(user: User) => dispatch({ type: "OPEN_DELETE", user })}
      />

      <CreateUserDialog
        open={dialog === "create"}
        onClose={() => dispatch({ type: "CLOSE_DIALOG" })}
        onSubmit={handleCreateUser}
        creating={creating}
      />

      <EditUserDialog
        open={dialog === "edit"}
        onClose={() => dispatch({ type: "CLOSE_DIALOG" })}
        onSubmit={handleUpdateUser}
        user={selectedUser}
        updating={updating}
      />

      <DeleteUserDialog
        open={dialog === "delete"}
        onClose={() => dispatch({ type: "CLOSE_DIALOG" })}
        onConfirm={handleDeleteUser}
        deleting={deleting}
      />
    </div>
  )
}

export default UserList
